import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { UiFlowProcessNodeComponent } from '../../molecules/flow-process-node/flow-process-node.component';
import { UiFlowTerminatorNodeComponent } from '../../molecules/flow-terminator-node/flow-terminator-node.component';
import { UiFlowDocumentNodeComponent } from '../../molecules/flow-document-node/flow-document-node.component';
import { UiFlowDecisionNodeComponent } from '../../molecules/flow-decision-node/flow-decision-node.component';
import { UiFlowConnectorNodeComponent } from '../../molecules/flow-connector-node/flow-connector-node.component';
import { UiFlowOffPageNodeComponent } from '../../molecules/flow-off-page-node/flow-off-page-node.component';
import { UiFlowIoNodeComponent } from '../../molecules/flow-io-node/flow-io-node.component';
import { UiFlowNoteNodeComponent } from '../../molecules/flow-note-node/flow-note-node.component';
import { UiFlowMarkerComponent } from '../../atoms/flow-marker/flow-marker.component';
import {
  buildFlowLayout,
  getFlowGridTrack,
  getFlowNodeCenter,
  getFlowNodeSize,
  type UiFlowNodeSize,
  type UiFlowPoint,
} from '../../shared/flow/flow-layout.util';
import {
  uiFlowEdgeHasMarkerMeta,
  type UiFlowEdge,
  type UiFlowNode,
} from '../../shared/flow/flow.types';

interface UiFlowRenderedEdge {
  id: string;
  path: string;
  labelX: number;
  labelY: number;
  marker?: UiFlowEdge['marker'];
  markerVariant: NonNullable<UiFlowEdge['markerVariant']>;
  label?: string;
  showMeta: boolean;
  dashed: boolean;
}

@Component({
  selector: 'ui-flow-diagram',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiFlowProcessNodeComponent,
    UiFlowTerminatorNodeComponent,
    UiFlowDocumentNodeComponent,
    UiFlowDecisionNodeComponent,
    UiFlowConnectorNodeComponent,
    UiFlowOffPageNodeComponent,
    UiFlowIoNodeComponent,
    UiFlowNoteNodeComponent,
    UiFlowMarkerComponent,
  ],
  templateUrl: './flow-diagram.component.html',
  styleUrl: './flow-diagram.component.scss',
  host: { class: 'ui-flow-diagram-host' },
})
export class UiFlowDiagramComponent {
  readonly nodes = input.required<readonly UiFlowNode[]>();
  readonly edges = input<readonly UiFlowEdge[]>([]);
  readonly title = input<string | null>(null);
  /** Emite cuando se hace click en un nodo con `interactive: true`. */
  readonly nodeClick = output<UiFlowNode>();
  protected readonly arrowMarkerId = `ui-flow-arrow-${Math.random().toString(36).slice(2, 9)}`;
  protected readonly layout = computed(() =>
    buildFlowLayout(this.nodes(), this.edges()),
  );
  protected readonly canvasWidth = computed(() => this.layout().width);
  protected readonly canvasHeight = computed(() => this.layout().height);

  protected readonly nodeById = computed(() => {
    const map = new Map<string, UiFlowNode>();
    for (const node of this.nodes()) map.set(node.id, node);
    return map;
  });

  protected readonly renderedEdges = computed(() => {
    const lookup = this.nodeById();
    const edges = this.edges();
    const pairCount = new Map<string, number>();
    for (const edge of edges) {
      const key = `${edge.from}\0${edge.to}`;
      pairCount.set(key, (pairCount.get(key) ?? 0) + 1);
    }

    const pairIndex = new Map<string, number>();
    const rendered: UiFlowRenderedEdge[] = [];

    for (const edge of edges) {
      const from = lookup.get(edge.from);
      const to = lookup.get(edge.to);
      if (!from || !to) continue;

      const key = `${edge.from}\0${edge.to}`;
      const index = pairIndex.get(key) ?? 0;
      pairIndex.set(key, index + 1);
      const count = pairCount.get(key) ?? 1;

      const route = this.buildEdgeRoute(from, to, index, count, edge);

      rendered.push({
        id: edge.id,
        path: route.path,
        labelX: route.mid.x,
        labelY: route.mid.y,
        marker: edge.marker,
        markerVariant: edge.markerVariant ?? 'icon',
        label: edge.label,
        showMeta: uiFlowEdgeHasMarkerMeta(edge),
        dashed: edge.style === 'dashed',
      });
    }

    return rendered;
  });

  protected trackNode(_: number, node: UiFlowNode): string {
    return node.id;
  }

  protected trackEdge(_: number, edge: UiFlowRenderedEdge): string {
    return edge.id;
  }

  protected gridTrack(track: number): number {
    return getFlowGridTrack(track);
  }

  protected onNodeClick(node: UiFlowNode): void {
    if (!node.interactive) return;
    this.nodeClick.emit(node);
  }

  protected onNodeKeydown(event: KeyboardEvent, node: UiFlowNode): void {
    if (!node.interactive) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.nodeClick.emit(node);
  }

  private cellCenter(col: number, row: number): { x: number; y: number } {
    return getFlowNodeCenter(this.layout(), col, row);
  }

  private buildEdgeRoute(
    from: UiFlowNode,
    to: UiFlowNode,
    parallelIndex = 0,
    parallelCount = 1,
    edge?: UiFlowEdge,
  ): { path: string; mid: UiFlowPoint } {
    const fromCenter = this.cellCenter(from.col, from.row);
    const toCenter = this.cellCenter(to.col, to.row);
    const fromSize = getFlowNodeSize(from.type);
    const toSize = getFlowNodeSize(to.type);

    if (edge?.route === 'gap-left' && from.col > 1) {
      return this.buildGapLeftRoute(from, fromCenter, toCenter, fromSize, toSize);
    }

    // Retorno vertical (misma columna hacia arriba): desvía a un lado.
    if (from.col === to.col && to.row < from.row) {
      return this.buildSideReturnRoute(fromCenter, toCenter, fromSize, toSize);
    }

    // Desde diamante hacia la izquierda con marker (p. ej. error → selfie):
    // sale del vértice inferior para no solaparse con la línea de avance.
    if (
      from.type === 'decision' &&
      from.row === to.row &&
      to.col < from.col &&
      !!edge?.marker
    ) {
      return this.buildBottomReturnRoute(fromCenter, toCenter, fromSize, toSize);
    }

    // Retorno horizontal (misma fila hacia la izquierda, saltando columnas).
    // Ramas adyacentes sin marker (p. ej. decisión → Empleado) usan línea normal.
    if (
      from.row === to.row &&
      to.col < from.col &&
      Math.abs(from.col - to.col) > 1
    ) {
      return this.buildBottomReturnRoute(fromCenter, toCenter, fromSize, toSize);
    }

    // Desde diamante hacia la derecha (misma fila):
    // - Rama única → vértice lateral (p. ej. No → Registrarse).
    // - Aristas paralelas al mismo destino → Sí derecha / No abajo.
    if (
      from.type === 'decision' &&
      from.row === to.row &&
      to.col > from.col
    ) {
      if (parallelCount > 1 && edge?.marker === 'error') {
        return this.buildDecisionBottomExitRoute(
          fromCenter,
          toCenter,
          fromSize,
          toSize,
        );
      }
      return this.buildDecisionRightExitRoute(
        fromCenter,
        toCenter,
        fromSize,
        toSize,
      );
    }

    if (parallelCount > 1 && from.row === to.row && to.col > from.col) {
      const offsetY =
        (parallelIndex - (parallelCount - 1) / 2) * 36;
      return this.buildOffsetHorizontalRoute(
        fromCenter,
        toCenter,
        fromSize,
        toSize,
        offsetY,
      );
    }

    if (parallelCount > 1 && from.col === to.col && to.row > from.row) {
      const offsetX =
        (parallelIndex - (parallelCount - 1) / 2) * 36;
      return this.buildOffsetVerticalRoute(
        fromCenter,
        toCenter,
        fromSize,
        toSize,
        offsetX,
      );
    }

    const { start, end } = this.trimToNodeBoundaries(
      fromCenter,
      toCenter,
      fromSize,
      toSize,
    );
    const elbow = { x: end.x, y: start.y };
    return {
      path: `M ${start.x} ${start.y} L ${elbow.x} ${elbow.y} L ${end.x} ${end.y}`,
      mid: this.pathMidpoint(start, elbow, end),
    };
  }

  /** Baja por el hueco entre columnas y entra al destino por la izquierda. */
  private buildGapLeftRoute(
    from: UiFlowNode,
    fromCenter: UiFlowPoint,
    toCenter: UiFlowPoint,
    fromSize: UiFlowNodeSize,
    toSize: UiFlowNodeSize,
  ): { path: string; mid: UiFlowPoint } {
    const leftCenter = this.cellCenter(from.col - 1, from.row);
    const laneX = (leftCenter.x + fromCenter.x) / 2;
    const start = {
      x: fromCenter.x - fromSize.width / 2,
      y: fromCenter.y,
    };
    const end = {
      x: toCenter.x - toSize.width / 2,
      y: toCenter.y,
    };
    const mid = { x: laneX, y: (start.y + end.y) / 2 };

    return {
      path: `M ${start.x} ${start.y} L ${laneX} ${start.y} L ${laneX} ${end.y} L ${end.x} ${end.y}`,
      mid,
    };
  }

  /** Correcto: sale del vértice derecho del diamante hacia el destino. */
  private buildDecisionRightExitRoute(
    fromCenter: UiFlowPoint,
    toCenter: UiFlowPoint,
    fromSize: UiFlowNodeSize,
    toSize: UiFlowNodeSize,
  ): { path: string; mid: UiFlowPoint } {
    const start = {
      x: fromCenter.x + fromSize.width / 2,
      y: fromCenter.y,
    };
    const end = {
      x: toCenter.x - toSize.width / 2,
      y: toCenter.y,
    };
    return {
      path: `M ${start.x} ${start.y} L ${end.x} ${end.y}`,
      mid: {
        x: (start.x + end.x) / 2,
        y: (start.y + end.y) / 2,
      },
    };
  }

  /**
   * Error: sale del vértice inferior del diamante, pasa por abajo
   * y entra al destino por abajo.
   */
  private buildDecisionBottomExitRoute(
    fromCenter: UiFlowPoint,
    toCenter: UiFlowPoint,
    fromSize: UiFlowNodeSize,
    toSize: UiFlowNodeSize,
  ): { path: string; mid: UiFlowPoint } {
    const laneOffset = 36;
    const start = {
      x: fromCenter.x,
      y: fromCenter.y + fromSize.height / 2,
    };
    const end = {
      x: toCenter.x,
      y: toCenter.y + toSize.height / 2,
    };
    const laneY = Math.max(start.y, end.y) + laneOffset;
    const mid = { x: (start.x + end.x) / 2, y: laneY };

    return {
      path: `M ${start.x} ${start.y} L ${start.x} ${laneY} L ${end.x} ${laneY} L ${end.x} ${end.y}`,
      mid,
    };
  }

  /** Línea horizontal paralela con desplazamiento vertical. */
  private buildOffsetHorizontalRoute(
    fromCenter: UiFlowPoint,
    toCenter: UiFlowPoint,
    fromSize: UiFlowNodeSize,
    toSize: UiFlowNodeSize,
    offsetY: number,
  ): { path: string; mid: UiFlowPoint } {
    const start = {
      x: fromCenter.x + fromSize.width / 2,
      y: fromCenter.y + offsetY,
    };
    const end = {
      x: toCenter.x - toSize.width / 2,
      y: toCenter.y + offsetY,
    };
    return {
      path: `M ${start.x} ${start.y} L ${end.x} ${end.y}`,
      mid: {
        x: (start.x + end.x) / 2,
        y: (start.y + end.y) / 2,
      },
    };
  }

  /** Línea vertical paralela con desplazamiento horizontal. */
  private buildOffsetVerticalRoute(
    fromCenter: UiFlowPoint,
    toCenter: UiFlowPoint,
    fromSize: UiFlowNodeSize,
    toSize: UiFlowNodeSize,
    offsetX: number,
  ): { path: string; mid: UiFlowPoint } {
    const start = {
      x: fromCenter.x + offsetX,
      y: fromCenter.y + fromSize.height / 2,
    };
    const end = {
      x: toCenter.x + offsetX,
      y: toCenter.y - toSize.height / 2,
    };
    return {
      path: `M ${start.x} ${start.y} L ${end.x} ${end.y}`,
      mid: {
        x: (start.x + end.x) / 2,
        y: (start.y + end.y) / 2,
      },
    };
  }

  /**
   * Ruta en U lateral: sale del origen, sube por un carril y entra al destino.
   * Prefiere izquierda; si el carril quedaría fuera del canvas (p. ej. col 1),
   * usa la derecha para que la línea no se corte.
   */
  private buildSideReturnRoute(
    fromCenter: UiFlowPoint,
    toCenter: UiFlowPoint,
    fromSize: UiFlowNodeSize,
    toSize: UiFlowNodeSize,
  ): { path: string; mid: UiFlowPoint } {
    const laneOffset = 40;
    const margin = 12;
    const canvasWidth = this.layout().width;

    const leftStart = {
      x: fromCenter.x - fromSize.width / 2,
      y: fromCenter.y,
    };
    const leftEnd = {
      x: toCenter.x - toSize.width / 2,
      y: toCenter.y,
    };
    const leftLane = Math.min(leftStart.x, leftEnd.x) - laneOffset;

    const rightStart = {
      x: fromCenter.x + fromSize.width / 2,
      y: fromCenter.y,
    };
    const rightEnd = {
      x: toCenter.x + toSize.width / 2,
      y: toCenter.y,
    };
    const rightLane = Math.max(rightStart.x, rightEnd.x) + laneOffset;

    const useLeft = leftLane >= margin;
    const start = useLeft ? leftStart : rightStart;
    const end = useLeft ? leftEnd : rightEnd;
    const laneX = useLeft
      ? leftLane
      : Math.min(rightLane, canvasWidth - margin);
    const mid = { x: laneX, y: (start.y + end.y) / 2 };

    return {
      path: `M ${start.x} ${start.y} L ${laneX} ${start.y} L ${laneX} ${end.y} L ${end.x} ${end.y}`,
      mid,
    };
  }

  /** Ruta en U por abajo: sale abajo, va a la izquierda y entra por abajo. */
  private buildBottomReturnRoute(
    fromCenter: UiFlowPoint,
    toCenter: UiFlowPoint,
    fromSize: UiFlowNodeSize,
    toSize: UiFlowNodeSize,
  ): { path: string; mid: UiFlowPoint } {
    const laneOffset = 40;
    const start = {
      x: fromCenter.x,
      y: fromCenter.y + fromSize.height / 2,
    };
    const end = {
      x: toCenter.x,
      y: toCenter.y + toSize.height / 2,
    };
    const laneY = Math.max(start.y, end.y) + laneOffset;
    const mid = { x: (start.x + end.x) / 2, y: laneY };

    return {
      path: `M ${start.x} ${start.y} L ${start.x} ${laneY} L ${end.x} ${laneY} L ${end.x} ${end.y}`,
      mid,
    };
  }

  /**
   * Recorta la línea centro-a-centro para que empiece y termine en el borde
   * de cada nodo. Así el tramo visible mide exactamente el gap calculado
   * (30px sin marker, 60px con marker) y el marker queda a la mitad.
   * Las rutas en L salen horizontales del origen y entran verticales al destino.
   */
  private trimToNodeBoundaries(
    start: UiFlowPoint,
    end: UiFlowPoint,
    fromSize: UiFlowNodeSize,
    toSize: UiFlowNodeSize,
  ): { start: UiFlowPoint; end: UiFlowPoint } {
    const a = { ...start };
    const b = { ...end };

    if (start.y === end.y && start.x !== end.x) {
      const dir = Math.sign(end.x - start.x);
      a.x += (fromSize.width / 2) * dir;
      b.x -= (toSize.width / 2) * dir;
    } else if (start.x === end.x && start.y !== end.y) {
      const dir = Math.sign(end.y - start.y);
      a.y += (fromSize.height / 2) * dir;
      b.y -= (toSize.height / 2) * dir;
    } else if (start.x !== end.x) {
      a.x += (fromSize.width / 2) * Math.sign(end.x - start.x);
      b.y -= (toSize.height / 2) * Math.sign(end.y - start.y);
    }

    return { start: a, end: b };
  }

  /** Punto a mitad de longitud del path ortogonal (start → elbow → end). */
  private pathMidpoint(
    start: { x: number; y: number },
    elbow: { x: number; y: number },
    end: { x: number; y: number },
  ): { x: number; y: number } {
    const seg1 = Math.hypot(elbow.x - start.x, elbow.y - start.y);
    const seg2 = Math.hypot(end.x - elbow.x, end.y - elbow.y);
    const half = (seg1 + seg2) / 2;

    if (half <= seg1 || seg2 === 0) {
      const t = seg1 === 0 ? 0 : half / seg1;
      return {
        x: start.x + (elbow.x - start.x) * t,
        y: start.y + (elbow.y - start.y) * t,
      };
    }

    const t = (half - seg1) / seg2;
    return {
      x: elbow.x + (end.x - elbow.x) * t,
      y: elbow.y + (end.y - elbow.y) * t,
    };
  }
}
